<template>
  <v-container ref="copyContainer" grid-list-xl fluid>
    <v-overlay :value="pageLoading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <template v-if="!pageLoading">
      <v-row class="mb-1">
        <v-col cols="12">
          <filter-revenue-stats
            :default-search-params="searchParams"
            :is-component-loading="isRequestingNow"
            @filtering="onFilter"
            @send-email-excel="onSendEmailExcel"
          ></filter-revenue-stats>
        </v-col>
      </v-row>

      <!-- Base orders without subscription -->
      <v-row v-if="dataBase" class="mb-4">
        <v-col cols="12">
          <h2>{{ $t('menu.orders_base') }}</h2>
        </v-col>
        <v-col cols="12" v-for="month in Object.keys(dataBase['stats'])" :key="'base_' + month">
          <v-card>
            <v-card-title class="cyan white--text">
              {{ formatMonth(month) }}
            </v-card-title>
            <v-divider />
            <v-simple-table>
              <template v-slot:default>
                <thead>
                <tr>
                  <th class="text-left">{{ $t('table.headers.day') }}</th>
                  <th class="text-left" v-for="(field, index) in dataBase['fields']" :key="'base_head_' + index">
                    {{ getHeader('base', field.value) }}
                  </th>
                </tr>
                </thead>
                <tbody>
                  <tr class="cyan--text" style="font-weight: bold;">
                    <td>
                      <v-icon color="cyan">mdi-cash-100</v-icon>
                    </td>
                    <td v-for="(field, index) in dataBase['fields']" :key="'base_total_' + field['name']">
                      {{ dataBase['totals'][month][field['name']] }}
                    </td>
                  </tr>
                  <tr v-for="(item, index) in dataBase['stats'][month]" :key="'base_item_' + item.day">
                    <td>{{ item.day }}</td>
                    <td v-for="(field, index) in dataBase['fields']" :key="'base_value_' + item.day + '_' + field['name']">
                      {{ item[field['name']] }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Complex orders without subscription -->
      <v-row v-if="dataComplex" class="mb-4">
        <v-col cols="12">
          <h2>{{ $t('menu.orders_complex') }}</h2>
        </v-col>
        <v-col cols="12" v-for="month in Object.keys(dataComplex['stats'])" :key="'complex_' + month">
          <v-card>
            <v-card-title class="cyan white--text">
              {{ formatMonth(month) }}
            </v-card-title>
            <v-divider />
            <v-simple-table>
              <template v-slot:default>
                <thead>
                <tr>
                  <th class="text-left">{{ $t('table.headers.day') }}</th>
                  <th class="text-left" v-for="(field, index) in dataComplex['fields']" :key="'complex_head_' + index">
                    {{ getHeader('complex', field.value) }}
                  </th>
                </tr>
                </thead>
                <tbody>
                  <tr class="cyan--text" style="font-weight: bold;">
                    <td>
                      <v-icon color="cyan">mdi-cash-100</v-icon>
                    </td>
                    <td v-for="(field, index) in dataComplex['fields']" :key="'complex_total_' + field['name']">
                      {{ dataComplex['totals'][month][field['name']] }}
                    </td>
                  </tr>
                  <tr v-for="(item, index) in dataComplex['stats'][month]" :key="'complex_item_' + item.day">
                    <td>{{ item.day }}</td>
                    <td v-for="(field, index) in dataComplex['fields']" :key="'complex_value_' + item.day + '_' + field['name']">
                      {{ item[field['name']] }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Base orders with subscription -->
      <v-row v-if="dataBaseSubscription" class="mb-4">
        <v-col cols="12">
          <h2>{{ $t('menu.orders_base') }} - {{ $t('table.headers.subscription') }}</h2>
        </v-col>
        <v-col cols="12" v-for="month in Object.keys(dataBaseSubscription['stats'])" :key="'basesub_' + month">
          <v-card>
            <v-card-title class="cyan white--text">
              {{ formatMonth(month) }}
            </v-card-title>
            <v-divider />
            <v-simple-table>
              <template v-slot:default>
                <thead>
                <tr>
                  <th class="text-left">{{ $t('table.headers.day') }}</th>
                  <th class="text-left" v-for="(field, index) in dataBaseSubscription['fields']" :key="'basesub_head_' + index">
                    {{ getHeader('base', field.value, field.with_additions) }}
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr class="cyan--text" style="font-weight: bold;">
                  <td>
                    <v-icon color="cyan">mdi-cash-100</v-icon>
                  </td>
                  <td v-for="(field, index) in dataBaseSubscription['fields']" :key="'basesub_total_' + field['name']">
                    {{ dataBaseSubscription['totals'][month][field['name']] }}
                  </td>
                </tr>
                <tr v-for="(item, index) in dataBaseSubscription['stats'][month]" :key="'basesub_item_' + item.day">
                  <td>{{ item.day }}</td>
                  <td v-for="(field, index) in dataBaseSubscription['fields']" :key="'basesub_value_' + item.day + '_' + field['name']">
                    {{ item[field['name']] }}
                  </td>
                </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>

    </template>
    <modal-loading :is-show="isTableLoading"></modal-loading>
  </v-container>
</template>

<script>
import moment from 'moment';
import i18n from '@/i18n'

/* constants */
import { MANAGER_TOP } from '@/constants/roles';

/* proxies */
import StatsRevenueProxy from '@/proxies/Stats/StatsRevenueProxy';

/* mixins */
import defaultListingMixin from '../../mixins/listing/defaultListingMixin';
import updatePageContentMixin from '../../mixins/updatePageContentMixin';
import downloadReport from '@/mixins/reports/downloadReport';
import sendEmailReport from '@/mixins/reports/sendEmailReport';

/* components */
import ModalLoading from '../../components/loaders/ModalLoading.vue';
import FilterRevenueStats from '../../components/listings/FilterRevenueStats.vue';
import { deepMerge } from "@/utils";

export default {
  name: 'RevenueStats',
  mixins: [updatePageContentMixin, defaultListingMixin, sendEmailReport],
  components: {
    ModalLoading,
    FilterRevenueStats,
  },
  data() {
    return {
      MANAGER_TOP,
      /* Page management */
      pageLoading: false,
      /* Listing management */
      isTableLoading: false,

      isDataBaseLoading: false,
      isDataComplexLoading: false,
      isDataBaseSubscriptionLoading: false,
      searchParams: {
        from: null,
        to: null,
        region: null,
        period: null,
        types: [],
      },
      emailReportMethodName: 'revenueStatsListingEmailReport',
      dataBase: null,
      dataComplex: null,
      dataBaseSubscription: null,
    };
  },
  computed: {
    /* store */
    account() {
      return this.$store.getters['account/account'];
    },
  },
  watch: {
    searchParams: {
      handler() {
        //mixin override
      },
      deep: true,
    },
  },
  methods: {
    onFilter(event) {
      let newSearchParams = Object.assign({}, event);

      newSearchParams = this.filterSearchParamsBeforeSend(newSearchParams);
      this.searchParams = deepMerge(this.searchParams, deepMerge(newSearchParams, {
        page: {
          number: 1,
        },
      }));

      this.isRequestingNow = true;
      this.fetchData();
    },
    fetchData() {
      this.isDataBaseLoading = true;
      this.isDataComplexLoading = true;
      //this.isDataBaseSubscriptionLoading = true;

      new StatsRevenueProxy(this.searchParams)
        .list('base')
        .then( ({ data: { data } }) => {
          this.dataBase = data.base;
        })
        .catch(error => {
          this.dataBase = null;
          console.error(error);
        })
        .finally(() => {
          this.isDataBaseLoading = false;
          this.updateLoadingStatus();
        });

      new StatsRevenueProxy(this.searchParams)
        .list('complex')
        .then( ({ data: { data } }) => {
          this.dataComplex = data.complex;
        })
        .catch(error => {
          this.dataComplex = null;
          console.error(error);
        })
        .finally(() => {
          this.isDataComplexLoading = false;
          this.updateLoadingStatus();
        });

      /*new StatsRevenueProxy(this.searchParams)
        .list('base_sub')
        .then( ({ data: { data } }) => {
          this.dataBaseSubscription = data.base_sub;
        })
        .catch(error => {
          this.dataBaseSubscription = null;
          console.error(error);
        })
        .finally(() => {
          this.isDataBaseSubscriptionLoading = false;
          this.updateLoadingStatus();
        });*/
    },
    formatMonth(month) {
      moment.locale(window.localStorage.language);
      const date = moment(month);
      return date.format('MMMM, YYYY');
    },
    getHeader(category, type, with_additions = false) {
      if (category === 'base') {
        let header = this.lang(this.$store, `sap/listings.global.filter.order_base_types.${type}`);
        if (with_additions) {
          header = header + ' (' + i18n.t('table.headers.additional_services') + ')';
        }
        return header;
      }
      if (category === 'complex') {
        let header = this.lang(this.$store, `sap/listings.global.filter.order_complex_types.${type}`);
        if (with_additions) {
          header = header + ' (' + i18n.t('table.headers.additional_services') + ')';
        }
        return header;
      }
      return type;
    },
    updateLoadingStatus() {
      if (!this.isDataBaseLoading && !this.isDataComplexLoading && !this.isDataBaseSubscriptionLoading ) {
        this.isTableLoading = false;
        this.isRequestingNow = false;

        this.$store.dispatch('page/finishUpdateCurrentPageContent');
      }
    },
  },
};
</script>

<style></style>
